import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import axiosClient from "../api/AxiosClient";
import { Agents } from "../types/master";
import { RootState } from ".";
import { safeJSONParse, safeParse } from "../utils/helper";
import { fetchAgents } from "./agentSlice";
import { getAgentStartedSteps } from "../utils/constants";

export interface ChatMessage {
  sessionId: string | null;
  id: string;
  sender: "user" | "agent";
  message: string;
  questionId?: string;
  timestamp: string;
  requestId?: string; // store agent requestId
  agentIdName?: string;
  workflowId?: string;
  files?: Array<{ name: string; size: number }>;
  feedback?: "liked" | "disliked" | null;
  isImageGeneration?: boolean;
  chatId?: number; // chat_id from API response for user preferences
  userPreference?: 0 | 1 | null; // 0 for disliked, 1 for liked, null for no preference
}

export interface StreamProgress {
  executorId: string;
  timestamp: number;
}

interface UserChatState {
  selectedAgent: Agents;
  chatHistory: { [agentId: number]: ChatMessage[] };
  currentChat: ChatMessage[];
  loading: boolean;
  workflowId: string;
  requestId: string;
  streamProgress: StreamProgress[];
  isStreaming: boolean;
  isViewingHistory: boolean;
}

const initialState: UserChatState = {
  selectedAgent: {} as Agents, // Will be set automatically when agents are fetched
  chatHistory: {},
  currentChat: [],
  loading: false,
  workflowId: "",
  requestId: "",
  streamProgress: [],
  isStreaming: false,
  isViewingHistory: false,
};

// ---------------- THUNK ----------------
export const sendMessageToAgent = createAsyncThunk(
  "user/sendMessageToAgent",
  async (
    {
      agentId,
      message,
      questionId,
      timestamp,
      agentEndpoint,
      sessionId,
      files,
      isImageGeneration = false,
      translation,
      translationType,
      procurement_type,
      accept_array,
      reject_array,
      modify_array,
      consolidator_accept_array,
      consolidator_reject_array,
      consolidator_modify_array,
      contract_reviewer_accept_array,
      contract_reviewer_reject_array,
      contract_reviewer_modify_array,
      contract_reviewer_new_array,
      add_new_array,
    }: {
      agentId: number;
      message: string;
      questionId: string;
      timestamp: string;
      agentEndpoint: string | undefined;
      sessionId: string | null;
      files?: File[];
      isImageGeneration?: boolean;
      translation?: "english" | "arabic";
      translationType?: "general" | "legal";
      procurement_type?: "sow_filler" | "reviewer" | "redraft";
      accept_array?: any[];
      reject_array?: any[];
      modify_array?: any[];
      consolidator_accept_array?: any[];
      consolidator_reject_array?: any[];
      consolidator_modify_array?: any[];
      contract_reviewer_accept_array?: string;
      contract_reviewer_reject_array?: string;
      contract_reviewer_modify_array?: string;
      contract_reviewer_new_array?: string;
      add_new_array?: any[];
    },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const currentUser = state.auth.user;
    const userEmail = currentUser?.username;

    // Use retained IDs from state
    const prevRequestId = state.user.requestId;
    const workflow_id = state.user.workflowId;

    // Get chatId from the last agent response in current chat
    const currentChat = state.user.currentChat;
    const lastAgentMessage = [...currentChat].reverse().find(msg => msg.sender === "agent");
    const previousChatId = lastAgentMessage?.chatId || null;

    // Get filters from state for agent ID 18
    const hrAgentFilters = state.filters.hrAgentFilters;

    // Build filters object for agent ID 18
    const buildFiltersForAgentId18 = () => {
      if (agentId !== 18) return null;

      const filters: Record<string, any> = {};

      if (hrAgentFilters.experience) {
        filters.experience = hrAgentFilters.experience.value;
      }

      if (hrAgentFilters.location && hrAgentFilters.location.length > 0) {
        filters.location = hrAgentFilters.location.map((loc) => loc.value);
      }

      if (hrAgentFilters.degree && hrAgentFilters.degree.length > 0) {
        filters.degree = hrAgentFilters.degree.map((deg) => deg.value);
      }

      if (
        hrAgentFilters.previousCompany &&
        hrAgentFilters.previousCompany.length > 0
      ) {
        filters.previousCompany = hrAgentFilters.previousCompany.map(
          (comp) => comp.value
        );
      }

      // Only return filters if at least one filter has a value
      return Object.keys(filters).length > 0 ? filters : null;
    };

    const filters = buildFiltersForAgentId18();

    const fileMetadata = files?.map((file) => ({
      name: file.name,
      size: file.size,
    }));

    const userMsg: ChatMessage = {
      sessionId,
      id: questionId,
      sender: "user",
      message,
      timestamp,
      files: fileMetadata,
      isImageGeneration,
    };

    try {
      // Check if this is a streaming endpoint
      const isStreamingEndpoint = agentEndpoint?.startsWith("/stream");

      // Prepare request data once (used for both streaming and non-streaming)
      let requestBody: FormData | string | Record<string, any>;
      const hasFiles = files && files.length > 0;

      if (hasFiles) {
        const formData = new FormData();
        formData.append("input", message);
        formData.append("user_email_id", userEmail || "");
        formData.append("agent_id", "0");
        formData.append("request_id", prevRequestId);
        formData.append("previous_chat_id", previousChatId !== null ? previousChatId.toString() : "");
        formData.append("is_approved", "0");
        formData.append("approved_by", "0");
        formData.append("score", "0");
        formData.append("workflow_id", workflow_id);
        formData.append("is_image_generation", isImageGeneration.toString());

        if (isStreamingEndpoint) {
          formData.append("response_type", "stream");
        }

        // Add filters for agent ID 18
        if (filters) {
          formData.append("filters", JSON.stringify(filters));
        }

        // Add translation settings for agent ID 7
        if (translation) {
          formData.append("translation", translation);
        }
        if (translationType) {
          formData.append("translation_type", translationType);
        }

        // Add procurement type for agent ID 20
        if (procurement_type) {
          formData.append("procurement_type", procurement_type);
        }

        // Add template reviewer feedback arrays (empty string if no items)
        formData.append(
          "accept_array",
          accept_array && accept_array.length > 0
            ? JSON.stringify(accept_array)
            : ""
        );
        formData.append(
          "reject_array",
          reject_array && reject_array.length > 0
            ? JSON.stringify(reject_array)
            : ""
        );
        formData.append(
          "modify_array",
          modify_array && modify_array.length > 0
            ? JSON.stringify(modify_array)
            : ""
        );

        // Add consolidator feedback arrays if present
        if (consolidator_accept_array && consolidator_accept_array.length > 0) {
          formData.append(
            "consolidator_accept_array",
            JSON.stringify(consolidator_accept_array)
          );
        }
        if (consolidator_reject_array && consolidator_reject_array.length > 0) {
          formData.append(
            "consolidator_reject_array",
            JSON.stringify(consolidator_reject_array)
          );
        }
        if (consolidator_modify_array && consolidator_modify_array.length > 0) {
          formData.append(
            "consolidator_modify_array",
            JSON.stringify(consolidator_modify_array)
          );
        }

        // Add contract reviewer feedback arrays if present
        if (contract_reviewer_accept_array) {
          formData.append(
            "contract_reviewer_accept_array",
            contract_reviewer_accept_array
          );
        }
        if (contract_reviewer_reject_array) {
          formData.append(
            "contract_reviewer_reject_array",
            contract_reviewer_reject_array
          );
        }
        if (contract_reviewer_modify_array) {
          formData.append(
            "contract_reviewer_modify_array",
            contract_reviewer_modify_array
          );
        }
        if (contract_reviewer_new_array) {
          formData.append(
            "contract_reviewer_new_array",
            contract_reviewer_new_array
          );
        }

        // Add new array for template reviewer checklist if present
        if (add_new_array && add_new_array.length > 0) {
          formData.append("add_new_array", JSON.stringify(add_new_array));
        }

        files.forEach((file) => {
          formData.append("files", file);
        });

        requestBody = formData;
      } else {
        const payload = {
          input: message,
          user_email_id: userEmail,
          agent_id: 0,
          request_id: prevRequestId,
          previous_chat_id: previousChatId !== null ? previousChatId : "",
          is_approved: 0,
          approved_by: 0,
          score: 0,
          workflow_id: workflow_id,
          is_image_generation: isImageGeneration,
          ...(isStreamingEndpoint && { response_type: "stream" }),
          ...(filters && { filters }),
          ...(translation && { translation }),
          ...(translationType && { translation_type: translationType }),
          ...(procurement_type && { procurement_type }),
          accept_array:
            accept_array && accept_array.length > 0
              ? JSON.stringify(accept_array)
              : "",
          reject_array:
            reject_array && reject_array.length > 0
              ? JSON.stringify(reject_array)
              : "",
          modify_array:
            modify_array && modify_array.length > 0
              ? JSON.stringify(modify_array)
              : "",
          ...(consolidator_accept_array?.length && {
            consolidator_accept_array: JSON.stringify(
              consolidator_accept_array
            ),
          }),
          ...(consolidator_reject_array?.length && {
            consolidator_reject_array: JSON.stringify(
              consolidator_reject_array
            ),
          }),
          ...(consolidator_modify_array?.length && {
            consolidator_modify_array: JSON.stringify(
              consolidator_modify_array
            ),
          }),
          ...(contract_reviewer_accept_array && {
            contract_reviewer_accept_array: contract_reviewer_accept_array,
          }),
          ...(contract_reviewer_reject_array && {
            contract_reviewer_reject_array: contract_reviewer_reject_array,
          }),
          ...(contract_reviewer_modify_array && {
            contract_reviewer_modify_array: contract_reviewer_modify_array,
          }),
          ...(contract_reviewer_new_array && {
            contract_reviewer_new_array: contract_reviewer_new_array,
          }),
          ...(add_new_array?.length && {
            add_new_array: JSON.stringify(add_new_array),
          }),
        };

        requestBody = isStreamingEndpoint ? JSON.stringify(payload) : payload;
      }

      if (isStreamingEndpoint) {
        // STREAMING LOGIC - For /stream/campaign, /stream/hr-workflow, /stream/hr-inference, /stream/jd-composer, and /stream/hr-screening endpoints
        const isHrWorkflowEndpoint =
          agentEndpoint === "/stream/hr-workflow" ||
          agentEndpoint === "/stream/hr-inference" ||
          agentEndpoint === "/stream/jd-composer" ||
          agentEndpoint === "/stream/hr-screening";

        let token: string | null = localStorage.getItem("access_token");

        let headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream", // Tell server we expect SSE
          "Cache-Control": "no-cache", // Prevent caching of stream
          Connection: "keep-alive", // Keep connection open for streaming
        };

        if (!hasFiles) {
          headers["Content-Type"] = "application/json";
        }

        console.log("Making streaming request with headers:", headers);

        // Make streaming request with proper configuration
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}${agentEndpoint}`,
          {
            method: "POST",
            headers,
            body: requestBody as FormData | string,
          }
        );

        console.log(
          "Stream response received:",
          response.status,
          response.headers.get("content-type")
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No reader available");
        }

        console.log("Stream reader created, starting to read chunks...");

        const decoder = new TextDecoder();
        let buffer = "";
        let lastCompletedPayload: any = null;
        let chunkCount = 0;

        // For HR workflow endpoint
        let hrWorkflowResult: any = null;
        let hrWorkflowId: string = "";

        // Clear previous stream progress
        dispatch(clearStreamProgress());

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log(
              `Stream completed. Total chunks received: ${chunkCount}`
            );
            break;
          }

          chunkCount++;
          console.log(
            `📦 Chunk ${chunkCount} received, size: ${value?.length} bytes`
          );

          // Decode the chunk
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Split buffer by newlines to process complete lines
          const lines = buffer.split("\n");

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || "";

          // Process each complete line
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            try {
              let event: any;

              // Handle SSE format (data: {...})
              if (trimmed.startsWith("data: ")) {
                const jsonStr = trimmed.substring(6);
                event = JSON.parse(jsonStr);
              }
              // Handle plain JSON
              else if (trimmed.startsWith("{")) {
                event = JSON.parse(trimmed);
              } else {
                continue;
              }

              console.log("Stream event received:", event);

              if (isHrWorkflowEndpoint) {
                // Handle HR workflow streaming events
                if (
                  (event.type === "start" || event.type === "modify") &&
                  event.agent_name
                ) {
                  console.log(
                    "HR Workflow event:",
                    event.type,
                    event.agent_name
                  );
                  hrWorkflowId = event.workflow_id || "";

                  // For modify events, dispatch "modify_requirements" instead of the actual agent
                  const executorId =
                    event.type === "modify"
                      ? "modify_requirements"
                      : event.agent_name;

                  dispatch(
                    addStreamProgress({
                      executorId: executorId,
                      timestamp: Date.now(),
                    })
                  );
                  // Small delay to ensure Redux state updates propagate
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }

                // Handle request_info events with result
                if (event.type === "request_info" && event.result) {
                  console.log("HR Workflow request_info received");
                  hrWorkflowResult = event.result;
                  hrWorkflowId = event.workflow_id || hrWorkflowId;
                }

                // Handle workflow_complete events
                if (event.type === "workflow_complete") {
                  console.log("HR Workflow completed");
                  hrWorkflowId = event.workflow_id || hrWorkflowId;
                }
              } else {
                // Handle campaign streaming events
                // Handle executor_invoked events for progress tracking
                if (event.type === "executor_invoked" && event.executor_id) {
                  console.log("Executor invoked:", event.executor_id);
                  dispatch(
                    addStreamProgress({
                      executorId: event.executor_id,
                      timestamp: Date.now(),
                    })
                  );
                  // Small delay to ensure Redux state updates propagate
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }

                // // Capture agent response messages (without "status" wrapper)
                // if (event.agent_id && event.result && !event.status && !event.type) {
                //   agentResponses.push(event);
                // }

                // Handle completed, awaiting_confirmation, awaiting_scoring_doc, criteria_parsed, or question_answered events with payload
                if ((event.status === "completed" || event.status === "awaiting_confirmation" || event.status === "awaiting_scoring_doc" || event.status === "criteria_parsed" || event.status === "question_answered") && event.payload) {
                  console.log(`${event.status} event received with payload`);
                  lastCompletedPayload = event.payload;
                }
              }
            } catch (e) {
              console.warn("Failed to parse stream line:", trimmed, e);
            }
          }
        }

        let finalResponseAnswer = "";
        let newRequestId = "";
        let agentIdName = "";
        let workflowId = "";
        let chatId: number | undefined = undefined;

        if (isHrWorkflowEndpoint) {
          // Handle HR workflow response
          console.log("HR Workflow Result:", hrWorkflowResult);
          workflowId = hrWorkflowId || workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            hrWorkflowResult?.requestId !== undefined
              ? hrWorkflowResult.requestId
              : prevRequestId;
          chatId = hrWorkflowResult?.chat_id;
          console.log("Extracted chatId for HR workflow:", chatId);

          // Check if this was a modify request by looking at streamProgress
          const currentStateCheck = getState() as RootState;
          const wasModifyRequest = currentStateCheck.user.streamProgress.some(
            (progress) => progress.executorId === "modify_requirements"
          );

          // Use "modify_requirements" as agentIdName if it was a modify request
          agentIdName = hrWorkflowResult?.data?.agent_name;

          const answer = hrWorkflowResult?.data?.output;
          const question = hrWorkflowResult?.data?.prompt;
          const stepNumber = hrWorkflowResult?.data?.step_num;

          finalResponseAnswer = JSON.stringify({
            answer,
            question,
            stepNumber,
          });
        } else {
          // Handle campaign workflow response
          console.log("Last Completed Payload:", lastCompletedPayload);
          workflowId = lastCompletedPayload?.workflow_id || workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            lastCompletedPayload?.result?.requestId !== undefined
              ? lastCompletedPayload.result.requestId
              : prevRequestId;
          chatId = lastCompletedPayload?.result?.chat_id;
          console.log("Extracted chatId for campaign workflow:", chatId);

          // Get the actual data from payload
          const payloadData = lastCompletedPayload?.result?.data;
          agentIdName = payloadData?.agent_id;

          const lastAgentId = payloadData?.agent_id;
          const question = payloadData?.question;
          const agentResponse = payloadData?.response;

          let finalResponse: any = {
            finalAnswer: "",
            brandReviewerAnswer: "",
            twitterAnswer: "",
            linkedinAnswer: "",
            instagramAnswer: "",
            images: "",
            socialMediaImages: "",
            socialMediaLocalizer: "",
            question: question,
          };

          if (lastAgentId === "utility_agent") {
            // if last agent id is utility_agent then just append the question result
            finalResponse = {
              ...finalResponse,
              finalAnswer: agentResponse[0]?.result,
            };
          } else {
            // else parse and send it to frontend
            if (lastAgentId === "press_release_agent") {
              // for press release agent
              const rawBrand = [...agentResponse].find(
                (item) => item.agent_id === "BrandReviewerAgent"
              )?.result;
              const rawPress = [...agentResponse].find(
                (item) => item.agent_id === "PressReleaseAgent"
              )?.result;
              const brandParsed = safeJSONParse(rawBrand);
              const pressParsed = safeJSONParse(rawPress);
              finalResponse = {
                ...finalResponse,
                finalAnswer: pressParsed,
                brandReviewerAnswer: brandParsed,
              };
            } else if (lastAgentId === "social_media_insights_agent") {
              const socialMediaImages = payloadData?.images;
              const rawTwitter = [...agentResponse].find(
                (item) => item.agent_id === "TwitterAgent"
              )?.result;
              const rawLinkedin = [...agentResponse].find(
                (item) => item.agent_id === "LinkedinAgent"
              )?.result;
              const rawInstagram = [...agentResponse].find(
                (item) => item.agent_id === "InstagramAgent"
              )?.result;
              const rawInsights = [...agentResponse].find(
                (item) => item.agent_id === "SocialMediaInsightsAgent"
              )?.result;
              const rawTwitterImages = [...agentResponse].find(
                (item) => item.agent_id === "twitter_image"
              )?.result;
              const rawLinkedinImages = [...agentResponse].find(
                (item) => item.agent_id === "linkedin_image"
              )?.result;
              const rawInstagramImages = [...agentResponse].find(
                (item) => item.agent_id === "instagram_image"
              )?.result;
              const rawTwitterLoc = [...agentResponse].find(
                (item) => item.agent_id === "SocialMediaLocalizerAgent_twitter"
              )?.result;
              const rawLinkedinLoc = [...agentResponse].find(
                (item) => item.agent_id === "SocialMediaLocalizerAgent_linkedin"
              )?.result;
              const rawInstagramLoc = [...agentResponse].find(
                (item) =>
                  item.agent_id === "SocialMediaLocalizerAgent_instagram"
              )?.result;

              const images = {
                rawTwitterImages,
                rawLinkedinImages,
                rawInstagramImages,
              };
              const socialMediaLocalizer = {
                rawTwitterLoc,
                rawLinkedinLoc,
                rawInstagramLoc,
              };
              const twitterParsed = safeJSONParse(rawTwitter);
              const linkedinParsed = safeJSONParse(rawLinkedin);
              const instagramParsed = safeJSONParse(rawInstagram);
              const insightsParsed = safeJSONParse(rawInsights);
              const imagesParsed = safeJSONParse(images);
              const socialMediaImagesParsed = safeJSONParse(socialMediaImages);
              const socialMediaLocalizerParsed =
                safeJSONParse(socialMediaLocalizer);

              finalResponse = {
                ...finalResponse,
                finalAnswer: insightsParsed,
                twitterAnswer: twitterParsed,
                linkedinAnswer: linkedinParsed,
                instagramAnswer: instagramParsed,
                images: imagesParsed,
                socialMediaImages: socialMediaImagesParsed,
                socialMediaLocalizer: socialMediaLocalizerParsed,
              };
            } else if (lastAgentId === "translation_agent") {
              const lastParsed = safeJSONParse(agentResponse);
              finalResponse = {
                ...finalResponse,
                finalAnswer: lastParsed,
              };
            } else if (lastAgentId === "consolidator_agent") {
              // Handle consolidator response
              // Three scenarios:
              // 1. Initial response: agentResponse has 2 items
              //    [0] = contract_reviewer_orchestrator (individual reviews)
              //    [1] = ConsolidatorAgent (consolidated negotiation playbook)
              // 2. Update response (after accept/reject): agentResponse has 1 item
              //    [0] = ConsolidatorAgent (updated consolidated negotiation playbook)
              // 3. QA response: agentResponse contains contract_reviewer_qa_agent

              // Check if this is a QA agent response
              const hasQAAgent = agentResponse?.some(
                (item: any) => item.agent_id === "contract_reviewer_qa_agent"
              );

              if (hasQAAgent) {
                // QA response scenario: preserve the response array
                finalResponse = {
                  ...finalResponse,
                  response: agentResponse,
                };
              } else {
                // Normal consolidator response
                let consolidatedSummary;

                // Check if this is an update (single item) or initial response (two items)
                if (
                  agentResponse?.length === 1 &&
                  agentResponse[0]?.agent_id === "ConsolidatorAgent"
                ) {
                  // Update scenario: only ConsolidatorAgent in response
                  consolidatedSummary = agentResponse[0]?.result || {};
                } else if (agentResponse?.length === 2) {
                  // Initial scenario: both individual reviews and consolidated summary
                  consolidatedSummary = agentResponse[1]?.result || {};
                } else {
                  // Fallback: try to find ConsolidatorAgent in the array
                  const consolidatorItem = agentResponse?.find(
                    (item: any) => item.agent_id === "ConsolidatorAgent"
                  );
                  consolidatedSummary = consolidatorItem?.result || {};
                }

                // Show only consolidated data in the response
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: consolidatedSummary,
                };
              }
            } else if (lastAgentId === "template_reviewer_agent") {
              // for other agents- localizer, etc
              const rawLast = agentResponse[0];
              const lastParsed = safeJSONParse(rawLast?.result);
              finalResponse = {
                ...finalResponse,
                finalAnswer: lastParsed,
              };
            } else if (lastAgentId === "rfq_orchestrator") {
              // Handle RFQ orchestrator response - preserve entire response array for Q&A
              // The response array contains both rfq_qa_agent and rfq_orchestrator results
              const orchestratorResult = agentResponse?.find(
                (item: any) => item.agent_id === "rfq_orchestrator"
              );

              finalResponse = {
                ...finalResponse,
                finalAnswer: {
                  ...orchestratorResult?.result,
                  response: agentResponse, // Preserve full response array including rfq_qa_agent
                },
              };
            } else {
              // for other agents- localizer, etc
              if (
                agentResponse &&
                Array.isArray(agentResponse) &&
                agentResponse.length > 0
              ) {
                const rawLast = agentResponse[agentResponse.length - 1];
                const lastParsed = safeJSONParse(rawLast?.result);
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: lastParsed,
                };
              }
            }
          }

          finalResponseAnswer = JSON.stringify(finalResponse);
        }

        const agentMsg: ChatMessage = {
          sessionId,
          id: uuid(),
          sender: "agent",
          message: finalResponseAnswer,
          questionId,
          timestamp: new Date().toISOString(),
          requestId: newRequestId,
          agentIdName,
          workflowId,
          chatId,
        };

        // Calculate delay to allow all progress steps to animate
        // Get current streamProgress from state
        const currentState = getState() as RootState;
        const allStreamProgress = currentState.user.streamProgress;

        // Import getStepsByAgentId to calculate total steps
        const { getAgentStartedSteps } = await import("../utils/constants");

        // Calculate total steps that will be shown
        let totalSteps = 1; // Start with 1 for initial "Thinking" step
        const processedAgents = new Set<string>();

        for (const progress of allStreamProgress) {
          if (!processedAgents.has(progress.executorId)) {
            processedAgents.add(progress.executorId);
            const agentSteps = getAgentStartedSteps(
              progress.executorId,
              isImageGeneration
            );
            totalSteps += agentSteps.length;
          }
        }

        return { agentId, userMsg, agentMsg };
      } else {
        // NON-STREAMING LOGIC - For all other endpoints
        console.log(`Making non-streaming request to: ${agentEndpoint}`);

        // Make normal axios API call using the prepared requestBody
        const response = await axiosClient.post(
          `${agentEndpoint}`,
          requestBody
        );

        // Log the response
        console.log("Non-streaming API Response:", response.data[0]);

        let finalResponseAnswer = "";
        let newRequestId = "";
        let agentIdName = "";
        let workflowId = "";
        let chatId: number | undefined = undefined;
        const agent_id = response.data[0]?.agent_id;
        const result = response.data[0]?.result;

        // chat_id should be at the same level as requestId
        // requestId is at result?.requestId, so chat_id should also be at result level
        chatId = result?.chat_id || response.data[0]?.chat_id;
        console.log(
          "Extracted chatId for non-streaming:",
          chatId,
          "from result:",
          result
        );

        if (agent_id === "hr_agent_v2_hitl") {
          workflowId = response.data[0]?.workflow_id || workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            result?.requestId !== undefined ? result.requestId : prevRequestId;
          agentIdName = result?.data?.agent_name;

          const answer = result?.data?.output;
          const question = result?.data?.prompt;
          const stepNumber = result?.data?.step_num;
          finalResponseAnswer = JSON.stringify({
            answer,
            question,
            stepNumber,
          });
        }

        // Create agent message with the response
        const agentMsg: ChatMessage = {
          sessionId,
          id: uuid(),
          sender: "agent",
          message: finalResponseAnswer,
          questionId,
          timestamp: new Date().toISOString(),
          requestId: newRequestId,
          agentIdName,
          workflowId,
          chatId,
        };

        return { agentId, userMsg, agentMsg };
      }
    } catch (err: any) {
      console.error("Error in sendMessageToAgent:", err);
      const agentMsg: ChatMessage = {
        sessionId,
        id: uuid(),
        sender: "agent",
        message: "Something went wrong",
        questionId,
        timestamp: new Date().toISOString(),
        requestId: prevRequestId,
      };
      return { agentId, userMsg, agentMsg };
    }
  }
);

// ---------------- USER PREFERENCE THUNK ----------------
export const sendUserPreferenceToAgent = createAsyncThunk(
  "user/sendUserPreferenceToAgent",
  async (
    {
      chatId,
      userPreference,
      messageId,
    }: {
      chatId: number;
      userPreference: 0 | 1; // 0 for dislike, 1 for like
      messageId: string; // To update the specific message after success
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const currentUser = state.auth.user;
      const userEmail = currentUser?.username;

      console.log("sendUserPreferenceToAgent invoked with:", {
        chatId,
        userPreference,
        messageId,
        userEmail,
      });

      if (!userEmail) {
        throw new Error("User email not found");
      }

      const requestBody = {
        chat_id: chatId,
        user_preference: userPreference,
        email_id: userEmail,
      };

      console.log("Sending request to /save_user_preferences:", requestBody);

      const response = await axiosClient.post(
        "/save_user_preferences",
        requestBody
      );

      console.log("Response from /save_user_preferences:", response.data);

      // Return the data we need to update the message
      return {
        messageId,
        userPreference,
        apiResponse: response.data,
      };
    } catch (err: any) {
      console.error("Error in sendUserPreferenceToAgent:", err);
      return rejectWithValue(err.response?.data || "Failed to save preference");
    }
  }
);

// ---------------- SLICE ----------------
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    selectAgent(state, action: PayloadAction<Agents>) {
      state.selectedAgent = action.payload;
      state.currentChat = state.chatHistory[action.payload.id] || [];

      // Reset retained IDs when switching agents for a fresh conversation
      state.workflowId = "";
      state.requestId = "";
      state.streamProgress = [];
      state.isStreaming = false;
      state.isViewingHistory = false;
    },
    resetRetainedIds(state) {
      // Manual reset if needed (e.g., starting a new conversation)
      state.workflowId = "";
      state.requestId = "";
      state.streamProgress = [];
      state.isStreaming = false;
      state.isViewingHistory = false;
      state.currentChat = []; // Clear current chat when exiting history mode
    },
    addStreamProgress(state, action: PayloadAction<StreamProgress>) {
      state.streamProgress.push(action.payload);
    },
    clearStreamProgress(state) {
      state.streamProgress = [];
    },
    setIsStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
    },
    clearAgentChat(state, action: PayloadAction<number>) {
      const agentId = action.payload;
      if (state.chatHistory[agentId]) {
        state.chatHistory[agentId] = [];
      }
      if (state.selectedAgent.id === agentId) {
        state.currentChat = [];
        state.isViewingHistory = false;
      }
    },
    loadHistoricalChat(
      state,
      action: PayloadAction<{
        messages: Array<{
          id: number;
          role: "user" | "assistant";
          content: string;
          timestamp: string;
          responseType: string | null;
          agentIdName?: string;
          chatId?: number;
          requestId?: string;
        }>;
        workflowId: string;
      }>
    ) {
      const { messages, workflowId } = action.payload;

      // Sort messages by timestamp in ascending order (oldest first)
      const sortedMessages = [...messages].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });

      // Map historical messages to ChatMessage format
      // Use uuid() to ensure unique keys for each message
      const historicalMessages: ChatMessage[] = sortedMessages.map((msg) => ({
        sessionId: workflowId,
        id: uuid(), // Generate unique ID for React key
        sender: msg.role === "assistant" ? "agent" : "user",
        message: msg.content,
        questionId: msg.id.toString(),
        timestamp: msg.timestamp,
        agentIdName: msg.agentIdName || msg.responseType || undefined,
        workflowId: workflowId,
        chatId: msg.chatId,
        requestId: msg.requestId,
      }));

      // Set historical chat as current chat
      state.currentChat = historicalMessages;
      state.workflowId = workflowId;
      state.loading = false;
      state.isStreaming = false;
      state.streamProgress = [];
      state.isViewingHistory = true; // Mark as viewing history
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendMessageToAgent.pending, (state, action) => {
      state.loading = true;
      state.isStreaming = true;
      state.streamProgress = [];
      state.isViewingHistory = false; // Clear history mode when sending new message

      const {
        agentId,
        message,
        questionId,
        timestamp,
        sessionId,
        files,
        isImageGeneration,
        translation,
        translationType,
      } = action.meta.arg;

      const fileMetadata = files?.map((file) => ({
        name: file.name,
        size: file.size,
      }));

      const userMsg: ChatMessage = {
        sessionId,
        id: questionId,
        sender: "user",
        message,
        questionId,
        timestamp,
        files: fileMetadata,
        isImageGeneration,
      };

      if (!state.chatHistory[agentId]) state.chatHistory[agentId] = [];
      state.chatHistory[agentId].push(userMsg);

      state.currentChat.push(userMsg);
    });

    builder.addCase(sendMessageToAgent.fulfilled, (state, action) => {
      state.loading = false;
      state.isStreaming = false;

      const { agentId, userMsg, agentMsg } = action.payload;

      if (!state.chatHistory[agentId]) state.chatHistory[agentId] = [];
      state.chatHistory[agentId].push(agentMsg);

      state.currentChat.push(agentMsg); // store agent message along with requestId

      // Update retained IDs - use new values if present, otherwise keep existing
      if (agentMsg.workflowId) {
        state.workflowId = agentMsg.workflowId;
      }
      if (agentMsg.requestId) {
        state.requestId = agentMsg.requestId;
      }
    });

    builder.addCase(sendMessageToAgent.rejected, (state, action) => {
      state.loading = false;
      state.isStreaming = false;
      state.streamProgress = [];
    });

    // Listen to fetchAgents from agentSlice and auto-select agent with id=11
    builder.addCase(fetchAgents.fulfilled, (state, action) => {
      const agents = action.payload;
      const defaultAgent = agents.find((agent) => agent.id === 11); // Select marketing campaign as default

      if (defaultAgent && !state.selectedAgent.id) {
        // Only set if no agent is currently selected
        state.selectedAgent = defaultAgent;
        state.currentChat = state.chatHistory[defaultAgent.id] || [];
      }
    });

    // Handle user preference API success
    builder.addCase(sendUserPreferenceToAgent.fulfilled, (state, action) => {
      const { messageId, userPreference } = action.payload;

      // Update in currentChat
      const currentChatMsg = state.currentChat.find(
        (msg) => msg.id === messageId
      );
      if (currentChatMsg) {
        currentChatMsg.userPreference = userPreference;
      }

      // Update in chatHistory for the selected agent
      const agentId = state.selectedAgent.id;
      if (state.chatHistory[agentId]) {
        const historyMsg = state.chatHistory[agentId].find(
          (msg) => msg.id === messageId
        );
        if (historyMsg) {
          historyMsg.userPreference = userPreference;
        }
      }
    });
  },
});

export const {
  selectAgent,
  resetRetainedIds,
  addStreamProgress,
  clearStreamProgress,
  setIsStreaming,
  clearAgentChat,
  loadHistoricalChat,
} = userSlice.actions;
export default userSlice.reducer;